import { useEffect, useState } from "react";
import {
  ArrowUpRight,
  Check,
  Mail,
  Phone,
  Menu,
  X,
  FileCheck2,
  Building2,
  Home,
  Zap,
  ShieldCheck,
  ClipboardCheck,
  Plus,
  Minus,
} from "lucide-react";

const NAV_LINKS = [
  { id: "services", label: "Services" },
  { id: "pricing", label: "Pricing" },
  { id: "about", label: "About" },
  { id: "faq", label: "FAQ" },
  { id: "contact", label: "Contact" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/80 border-b border-black/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <a
          href="#top"
          data-testid="logo-link"
          className="flex items-center gap-2 group"
        >
          <img
            src="/logo.png"
            alt="T24 Calcs"
            className="h-12 w-auto"
            style={{ filter: "invert(1)" }}
          />
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              data-testid={`nav-${l.id}`}
              className="text-sm font-medium text-[#020617]/80 hover:text-[#0055FF] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:block">
          <a
            href="#contact"
            data-testid="nav-cta-quote"
            className="btn-primary text-sm"
          >
            Get a Quote <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>

        <button
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden p-2 border border-black/10"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-black/5 bg-white">
          <div className="px-6 py-4 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                data-testid={`mobile-nav-${l.id}`}
                onClick={() => setOpen(false)}
                className="text-sm font-medium text-[#020617]"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              data-testid="mobile-nav-cta"
              onClick={() => setOpen(false)}
              className="btn-primary justify-center"
            >
              Get a Quote <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 pb-20 lg:pt-44 lg:pb-28 overflow-hidden"
    >
      <div className="absolute inset-0 blueprint-bg opacity-60 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-end">
          <div className="lg:col-span-7 section-reveal">
            <div className="flex items-center gap-3 mb-8">
              <span className="overline">California · Title 24</span>
              <span className="h-px w-12 bg-[#020617]/30" />
              <span className="overline">CHEERS Certified</span>
            </div>
            <h1
              data-testid="hero-heading"
              className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter text-[#020617] leading-[0.95]"
            >
              California Title&nbsp;24
              <br />
              <span className="text-[#0055FF]">Energy Compliance</span>
              <br />
              Done Right.
            </h1>
            <p className="mt-8 text-lg sm:text-xl text-[#475569] max-w-2xl leading-relaxed">
              Fast, accurate, CHEERS-certified CF1R reports for Single Family,
              Multifamily, and ADU projects across California. Built for
              architects, builders, and homeowners who need it done right the
              first time.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#contact"
                data-testid="hero-cta-quote"
                className="btn-primary"
              >
                Get a Quote <ArrowUpRight className="w-4 h-4" />
              </a>
              <a
                href="#pricing"
                data-testid="hero-cta-pricing"
                className="btn-secondary"
              >
                See Pricing
              </a>
            </div>

            <div className="mt-14 grid grid-cols-3 gap-6 max-w-xl">
              <div>
                <div className="font-display font-bold text-3xl text-[#020617]">
                  48h
                </div>
                <div className="text-xs uppercase tracking-widest text-[#475569] mt-1">
                  Typical Turnaround
                </div>
              </div>
              <div>
                <div className="font-display font-bold text-3xl text-[#020617]">
                  CF1R
                </div>
                <div className="text-xs uppercase tracking-widest text-[#475569] mt-1">
                  Compliance Docs
                </div>
              </div>
              <div>
                <div className="font-display font-bold text-3xl text-[#020617]">
                  CA
                </div>
                <div className="text-xs uppercase tracking-widest text-[#475569] mt-1">
                  Statewide
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 section-reveal">
            <div className="grid grid-cols-2 grid-rows-6 gap-3 h-[480px] lg:h-[560px]">
              <div className="col-span-2 row-span-4 relative overflow-hidden grid-border">
                <img
                  src="https://images.unsplash.com/photo-1759355787121-eaef014a501d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjYWxpZm9ybmlhJTIwaG9tZSUyMGV4dGVyaW9yfGVufDB8fHx8MTc3ODAxMjE2Mnww&ixlib=rb-4.1.0&q=85"
                  alt="Modern California home"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-5">
                  <div className="text-white/90 text-xs uppercase tracking-[0.24em] font-bold">
                    Residential
                  </div>
                  <div className="text-white font-display font-semibold text-xl mt-1">
                    Single Family CF1R
                  </div>
                </div>
              </div>
              <div className="col-span-1 row-span-2 bg-[#020617] text-white p-5 flex flex-col justify-between grid-border">
                <FileCheck2 className="w-6 h-6" />
                <div>
                  <div className="overline text-white/60">Doc</div>
                  <div className="font-display font-semibold text-base mt-1">
                    CF1R Compliant
                  </div>
                </div>
              </div>
              <div className="col-span-1 row-span-2 bg-[#0055FF] text-white p-5 flex flex-col justify-between grid-border">
                <ShieldCheck className="w-6 h-6" />
                <div>
                  <div className="overline text-white/70">Cert</div>
                  <div className="font-display font-semibold text-base mt-1">
                    CHEERS
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Services = () => {
  const services = [
    {
      icon: Home,
      kicker: "01",
      title: "Single Family & ADU",
      body: "Full Title 24 energy compliance analysis and CF1R documentation for new construction, additions, alterations, and Accessory Dwelling Units of any size.",
      bullets: [
        "CF1R Certificate of Compliance",
        "Envelope, HVAC & water heating analysis",
        "ADUs (attached, detached, or JADU)",
        "Fast 48-hour typical turnaround",
      ],
    },
    {
      icon: Building2,
      kicker: "02",
      title: "Multifamily Projects",
      body: "Per-unit precision for apartments, townhomes, condos, and mixed-use. Scaled pricing and consistent documentation from two units to two hundred.",
      bullets: [
        "Per-unit CF1R modeling",
        "Low-rise & high-rise multifamily",
        "CHEERS registration",
      ],
    },
  ];

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 lg:py-32 border-t border-black/10 bg-[#F3F4F6]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <div className="overline">Services</div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-[#020617] mt-4 max-w-3xl">
              Compliance reports we handle.
            </h2>
          </div>
          <p className="text-[#475569] max-w-md text-base leading-relaxed">
            Two focused offerings. Both CHEERS certified. Both delivered with
            the accuracy your plan-check needs.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {services.map((s, idx) => {
            const Icon = s.icon;
            return (
              <div
                key={idx}
                data-testid={`service-card-${idx}`}
                className="grid-border bg-[#FDFDFD] p-8 lg:p-10 flex flex-col"
              >
                <div className="flex items-center justify-between mb-8">
                  <Icon className="w-8 h-8 text-[#0055FF]" strokeWidth={1.5} />
                  <span className="overline">{s.kicker}</span>
                </div>
                <h3 className="font-display font-semibold text-2xl lg:text-3xl text-[#020617] tracking-tight">
                  {s.title}
                </h3>
                <p className="mt-4 text-[#475569] leading-relaxed">{s.body}</p>
                <ul className="mt-8 space-y-3 border-t border-black/10 pt-6">
                  {s.bullets.map((b, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3 text-sm text-[#020617]"
                    >
                      <Check
                        className="w-4 h-4 mt-0.5 text-[#0055FF] flex-shrink-0"
                        strokeWidth={2.5}
                      />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Pricing = () => {
  const tiers = [
    {
      id: "sf-small",
      eyebrow: "Single Family",
      title: "Under 3,000 sq ft",
      price: "300",
      unit: "flat",
      features: [
        "CF1R Certificate of Compliance",
        "Envelope + HVAC analysis",
        "Revisions included",
        "CHEERS registration",
      ],
    },
    {
      id: "sf-large",
      eyebrow: "Single Family",
      title: "Over 3,000 sq ft",
      price: "600",
      unit: "flat",
      features: [
        "Everything in the smaller tier",
        "Expanded envelope analysis",
        "Priority turnaround",
        "CHEERS registration",
      ],
    },
    {
      id: "multifamily",
      eyebrow: "Multifamily",
      title: "Per-unit pricing",
      price: "300",
      unit: "per unit",
      highlighted: true,
      features: [
        "Per-unit CF1R modeling",
        "Low-rise & high-rise",
        "Volume scaling",
        "CHEERS registration",
      ],
    },
    {
      id: "adu",
      eyebrow: "ADU",
      title: "Any size",
      price: "300",
      unit: "flat",
      features: [
        "Accessory Dwelling Units",
        "Attached, detached, or JADU",
        "Any square footage",
        "CHEERS registration",
      ],
    },
  ];

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="py-24 lg:py-32 border-t border-black/10 bg-[#FDFDFD]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <div className="overline">Pricing</div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-[#020617] mt-4 max-w-3xl">
              Transparent, flat-rate pricing.
            </h2>
          </div>
          <p className="text-[#475569] max-w-md text-base leading-relaxed">
            No hidden fees. What you see is what you pay. Complex or unique
            projects — reach out for a custom quote.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {tiers.map((t) => {
            const highlighted = t.highlighted;
            return (
              <div
                key={t.id}
                data-testid={`pricing-${t.id}`}
                className={`grid-border p-8 lg:p-10 flex flex-col ${
                  highlighted
                    ? "bg-[#020617] text-white border-[#020617]"
                    : "bg-[#FDFDFD] text-[#020617]"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`text-xs uppercase tracking-[0.24em] font-bold ${
                      highlighted ? "text-white/60" : "text-[#475569]"
                    }`}
                  >
                    {t.eyebrow}
                  </span>
                  {highlighted && (
                    <span className="text-xs uppercase tracking-[0.2em] font-bold bg-[#0055FF] text-white px-2 py-1">
                      Most Common
                    </span>
                  )}
                </div>

                <h3
                  className={`font-display font-semibold text-2xl mt-6 ${
                    highlighted ? "text-white" : "text-[#020617]"
                  }`}
                >
                  {t.title}
                </h3>

                <div className="mt-8 flex items-baseline gap-2">
                  <span
                    className={`font-display font-black text-6xl tracking-tighter ${
                      highlighted ? "text-white" : "text-[#020617]"
                    }`}
                  >
                    ${t.price}
                  </span>
                  <span
                    className={`text-sm ${
                      highlighted ? "text-white/70" : "text-[#475569]"
                    }`}
                  >
                    {t.unit}
                  </span>
                </div>

                <ul
                  className={`mt-8 space-y-3 border-t pt-6 ${
                    highlighted ? "border-white/15" : "border-black/10"
                  }`}
                >
                  {t.features.map((f, i) => (
                    <li
                      key={i}
                      className={`flex items-start gap-3 text-sm ${
                        highlighted ? "text-white/90" : "text-[#020617]"
                      }`}
                    >
                      <Check
                        className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                          highlighted ? "text-[#7aa8ff]" : "text-[#0055FF]"
                        }`}
                        strokeWidth={2.5}
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="#contact"
                  data-testid={`pricing-cta-${t.id}`}
                  className={`mt-10 inline-flex items-center justify-center gap-2 px-5 py-3 font-semibold text-sm border transition-colors ${
                    highlighted
                      ? "bg-white text-[#020617] border-white hover:bg-[#F3F4F6]"
                      : "bg-transparent text-[#020617] border-[#020617] hover:bg-[#020617] hover:text-white"
                  }`}
                >
                  Request This <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const About = () => {
  const points = [
    {
      icon: Zap,
      title: "California Specific",
      body: "Deep working knowledge of the California Energy Code and Title 24 Part 6 — residential and multifamily.",
    },
    {
      icon: ClipboardCheck,
      title: "Builder-Friendly",
      body: "Clean CF1R documentation formatted for plan-check, CHEERS registered, and revision-ready.",
    },
    {
      icon: ShieldCheck,
      title: "Rigorous Accuracy",
      body: "Every calculation double-checked. No surprises when your project hits the permit desk.",
    },
  ];

  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-24 lg:py-32 border-t border-black/10 bg-[#F3F4F6]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <div className="overline">About</div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-[#020617] mt-4">
              Why T24 Calcs.
            </h2>
            <p className="mt-6 text-[#475569] text-lg leading-relaxed">
              T24 Calcs is a California-focused Title 24 consultancy. We do one
              thing — CF1R energy compliance reports — and we do it with
              precision, speed, and clean documentation that passes plan-check
              the first time.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {points.map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={i}
                  data-testid={`about-point-${i}`}
                  className="grid-border bg-[#FDFDFD] p-6 flex flex-col gap-4"
                >
                  <Icon
                    className="w-7 h-7 text-[#0055FF]"
                    strokeWidth={1.5}
                  />
                  <h3 className="font-display font-semibold text-lg text-[#020617]">
                    {p.title}
                  </h3>
                  <p className="text-sm text-[#475569] leading-relaxed">
                    {p.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

const FAQ = () => {
  const items = [
    {
      q: "What is a Title 24 / CF1R report?",
      a: "Title 24 Part 6 is California's Building Energy Efficiency Standards. A CF1R (Certificate of Compliance — Residential) is the document we produce to prove your project meets those standards. It's required by most California jurisdictions before a building permit will be issued.",
    },
    {
      q: "How long does a Title 24 report take?",
      a: "Most Single Family CF1R reports are delivered within 48 hours of receiving complete plans. Multifamily timelines depend on unit count — typically 3 to 7 business days. Need it faster? Mention it when you reach out and we'll do our best to accommodate.",
    },
    {
      q: "Do you serve all of California?",
      a: "Yes. We prepare CF1R reports for projects in every California county — Los Angeles, Orange, San Diego, the Bay Area, Sacramento, Central Valley, Inland Empire, and beyond.",
    },
    {
      q: "What do you need from me to get started?",
      a: "A complete set of architectural plans (floor plans, elevations, window schedule, and door schedule with U-factor and SHGC if applicable). For multifamily, also include the unit mix. Optionally — and helpful for accuracy — you can also send: specific HVAC and water heater equipment you'd like to use, attic details, wall/ceiling/slab R-value insulation, and window U-factor / SHGC. If any of the optional items aren't provided, I'll use best-practice assumptions. Email everything to shlema@t24calcs.com and we'll send a confirmed quote within the same business day.",
    },
    {
      q: "Are revisions included in the price?",
      a: "Yes. Reasonable revisions during the design and plan-check process are included in the flat rate. We work with you until the report is approved by your jurisdiction.",
    },
    {
      q: "What is CHEERS registration and is it included?",
      a: "CHEERS (California Home Energy Efficiency Rating System) is one of the state-approved registries for Title 24 compliance documents. Yes — CHEERS registration is included in every report we deliver.",
    },
  ];

  const [open, setOpen] = useState(0);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="py-24 lg:py-32 border-t border-black/10 bg-[#FDFDFD]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4">
            <div className="overline">FAQ</div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-[#020617] mt-4">
              Common questions.
            </h2>
            <p className="mt-6 text-[#475569] leading-relaxed">
              Quick answers to what we get asked most. Don't see yours? Email or
              call — we reply same-day.
            </p>
          </div>
          <div className="lg:col-span-8">
            <div className="border-t border-black/10">
              {items.map((item, i) => {
                const isOpen = open === i;
                return (
                  <div
                    key={i}
                    data-testid={`faq-item-${i}`}
                    className="border-b border-black/10"
                  >
                    <button
                      onClick={() => setOpen(isOpen ? -1 : i)}
                      data-testid={`faq-toggle-${i}`}
                      aria-expanded={isOpen}
                      className="w-full flex items-start justify-between gap-6 py-6 text-left group"
                    >
                      <span className="font-display font-semibold text-lg sm:text-xl text-[#020617] group-hover:text-[#0055FF] transition-colors">
                        {item.q}
                      </span>
                      <span
                        className={`mt-1 flex-shrink-0 w-8 h-8 border border-black/15 flex items-center justify-center transition-colors ${
                          isOpen
                            ? "bg-[#020617] text-white border-[#020617]"
                            : "bg-transparent text-[#020617]"
                        }`}
                      >
                        {isOpen ? (
                          <Minus className="w-4 h-4" strokeWidth={2.5} />
                        ) : (
                          <Plus className="w-4 h-4" strokeWidth={2.5} />
                        )}
                      </span>
                    </button>
                    {isOpen && (
                      <div
                        data-testid={`faq-answer-${i}`}
                        className="pb-6 pr-12 text-[#475569] leading-relaxed"
                      >
                        {item.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Contact = () => {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#020617] text-white py-24 lg:py-36 border-t border-black/10"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="overline text-white/60">Contact</div>
        <h2 className="mt-6 font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] max-w-5xl">
          Ready for your
          <br />
          <span className="text-[#7aa8ff]">compliance report?</span>
        </h2>
        <p className="mt-8 text-lg text-white/70 max-w-2xl leading-relaxed">
          Send over your plans or give a quick call. Most quotes returned the
          same day.
        </p>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          <a
            href="mailto:shlema@t24calcs.com"
            data-testid="contact-email-link"
            className="group block border-t border-white/20 pt-8"
          >
            <div className="flex items-center gap-3 text-white/60 text-xs uppercase tracking-[0.24em] font-bold mb-6">
              <Mail className="w-4 h-4" /> Email
            </div>
            <span className="mega-link text-3xl sm:text-4xl lg:text-5xl">
              shlema@t24calcs.com
            </span>
            <div className="mt-6 flex items-center gap-2 text-sm text-white/70 group-hover:text-white transition-colors">
              Send an email <ArrowUpRight className="w-4 h-4" />
            </div>
          </a>

          <a
            href="tel:+18186196236"
            data-testid="contact-phone-link"
            className="group block border-t border-white/20 pt-8"
          >
            <div className="flex items-center gap-3 text-white/60 text-xs uppercase tracking-[0.24em] font-bold mb-6">
              <Phone className="w-4 h-4" /> Phone
            </div>
            <span className="mega-link text-4xl sm:text-5xl">
              (818) 619-6236
            </span>
            <div className="mt-6 flex items-center gap-2 text-sm text-white/70 group-hover:text-white transition-colors">
              Call now <ArrowUpRight className="w-4 h-4" />
            </div>
          </a>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#020617] text-white/70 border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="T24 Calcs" className="h-12 w-auto" />
        </div>
        <div className="text-xs uppercase tracking-[0.24em]">
          California Title 24 · CF1R · CHEERS Certified
        </div>
        <div className="text-xs text-white/50">
          © {new Date().getFullYear()} T24 Calcs. All rights reserved.
        </div>
      </div>
    </footer>
  );
};

export default function LandingPage() {
  return (
    <div data-testid="landing-page" className="min-h-screen bg-[#FDFDFD]">
      <Header />
      <main>
        <Hero />
        <Services />
        <Pricing />
        <About />
        <FAQ />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}
